import { getCurrentMemberPointsLimit, getCurrTeamInfo, getUserTeams } from '@/api/team'

const emptyState = () => ({
  teams: [],
  currentTeam: {},
  currentTeamInfo: {},
  memberPointsLimit: {},
  contextRequestId: 0,
  loading: false,
  loaded: false
})

const state = emptyState

const getters = {
  teams: state => state.teams,
  currentTeam: state => state.currentTeam,
  currentTeamInfo: state => state.currentTeamInfo,
  memberPointsLimit: state => state.memberPointsLimit,
  personalTeam: state => state.teams.find(item => Number(item.team_type) > 0) || {},
  enterpriseTeams: state => state.teams.filter(item => Number(item.team_type) === 0),
  teamRoles: state => Array.isArray(state.currentTeam.team_roles) ? state.currentTeam.team_roles : [],
  isEnterprise: state => Number(state.currentTeam.team_type) === 0,
  canManage: (state, getters) => getters.isEnterprise && getters.teamRoles.length > 0,
  isOwner: (state, getters, rootState, rootGetters) => {
    const currentTeamId = Number(rootGetters['user/current_team_id'])
    const currentUserId = Number(rootGetters['user/user_id'])
    if (!currentTeamId || !currentUserId) return false

    const userTeam = rootGetters['user/current_team'] || {}
    const team = [state.currentTeamInfo, state.currentTeam, userTeam]
      .find(item => Number(item.team_id || item.id) === currentTeamId &&
        (Number(item.belong_user_id) > 0 || Object.prototype.hasOwnProperty.call(item, 'is_owner')))
    if (!team) return false
    if (Number(team.belong_user_id) > 0) return Number(team.belong_user_id) === currentUserId
    return team.is_owner === true || Number(team.is_owner) === 1
  },
  canManageSubscription: (state, getters) => {
    const isDomestic = String(process.env.VUE_APP_LEGAL_REGION || '').toLowerCase() === 'domestic'
    return isDomestic || getters.isOwner
  },
  teamPointsSurplus: state => {
    const numericalAuth = state.currentTeamInfo.numerical_auth || {}
    const pointsQuota = numericalAuth.points_quota || numericalAuth
    return Number(pointsQuota.points_quota_surplus_numerical) || 0
  },
  displayPoints: (state, getters) => {
    if (Number(state.memberPointsLimit.points_limit_enabled) === 1) {
      return Number(state.memberPointsLimit.points_remaining_numerical) || 0
    }
    return getters.teamPointsSurplus
  }
}

const mutations = {
  setLoading(state, loading) {
    state.loading = loading
  },
  setTeams(state, teams) {
    state.teams = Array.isArray(teams) ? teams : []
  },
  setCurrentTeam(state, team) {
    state.currentTeam = team || {}
  },
  setCurrentTeamInfo(state, teamInfo) {
    state.currentTeamInfo = teamInfo || {}
  },
  setMemberPointsLimit(state, memberPointsLimit) {
    state.memberPointsLimit = memberPointsLimit || {}
  },
  startContextRequest(state) {
    state.contextRequestId += 1
  },
  setLoaded(state, loaded) {
    state.loaded = loaded
  },
  reset(state) {
    const contextRequestId = state.contextRequestId + 1
    Object.assign(state, emptyState(), { contextRequestId })
  }
}

const actions = {
  async load({ commit, dispatch, rootGetters }) {
    commit('setLoading', true)
    try {
      const response = await getUserTeams({})
      const teams = response && response.code === 0 && Array.isArray(response.data)
        ? response.data
        : []
      const currentTeamId = Number(rootGetters['user/current_team_id'])
      commit('setTeams', teams)
      commit('setCurrentTeam', teams.find(item => Number(item.team_id) === currentTeamId) || {})
      await dispatch('loadCurrentContext')
      commit('setLoaded', true)
      return teams
    } finally {
      commit('setLoading', false)
    }
  },
  async loadCurrentContext({ state, commit, rootGetters }) {
    const currentTeamId = Number(rootGetters['user/current_team_id'])
    const currentUserId = Number(rootGetters['user/user_id'])
    if (!rootGetters['user/accessToken'] || !currentTeamId) return

    commit('startContextRequest')
    const contextRequestId = state.contextRequestId
    const [teamResponse, memberLimitResponse] = await Promise.all([
      getCurrTeamInfo({}).catch(() => null),
      getCurrentMemberPointsLimit({}).catch(() => null)
    ])

    // 仅提交当前登录用户、当前团队的最新请求结果。
    if (contextRequestId !== state.contextRequestId ||
        !rootGetters['user/accessToken'] ||
        currentTeamId !== Number(rootGetters['user/current_team_id']) ||
        currentUserId !== Number(rootGetters['user/user_id'])) return

    if (teamResponse && teamResponse.code === 0 && teamResponse.data) {
      commit('setCurrentTeamInfo', teamResponse.data)
    }
    if (memberLimitResponse && memberLimitResponse.code === 0 && memberLimitResponse.data) {
      commit('setMemberPointsLimit', memberLimitResponse.data)
    }
  },
  reset({ commit }) {
    commit('reset')
  }
}

export default { namespaced: true, state, getters, mutations, actions }
